/**
 * Comprehensive Injection Attack Detection
 * 
 * This module provides detection patterns for all major injection attack types:
 * - SQL Injection
 * - NoSQL Injection
 * - Command/OS Shell Injection
 * - LDAP Injection
 * - XPath Injection
 * - XML/XXE Injection
 * - Template Injection (SSTI)
 * - Expression Language Injection
 * - CRLF Injection
 * - Host Header Injection
 * - HTTP Parameter Pollution
 * - SMTP Injection
 * - JSON Injection
 * - GraphQL Injection
 * - Header Injection
 * - CSS Injection
 * - HTML Injection
 * - DOM Clobbering
 * - Deserialization Injection
 * - Path Traversal
 * - YAML Injection
 * - CSV Formula Injection
 * - Regex Injection (ReDoS)
 * - Prototype Pollution
 * - Format String Injection
 * - Unicode/Homoglyph Injection
 * - SSI Injection
 * - Log Injection
 */

import logger from './logger';

/**
 * Detection result interface
 */
export interface InjectionDetectionResult {
  isSafe: boolean;
  threats: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    pattern: string;
    description: string;
  }>;
  severity: 'safe' | 'low' | 'medium' | 'high' | 'critical';
}

/**
 * SQL Injection Patterns
 */
const SQL_INJECTION_PATTERNS = [
  {
    pattern: /(\bOR\b|\bAND\b)\s*[\d\w]+\s*=\s*[\d\w]+/gi,
    type: 'SQL Injection',
    severity: 'critical',
    description: 'Boolean-based SQL injection detected'
  },
  {
    pattern: /'?\s*(OR|AND)\s+'?\d+'?\s*=\s*'?\d+'?/gi,
    type: 'SQL Injection',
    severity: 'critical',
    description: 'SQL injection with boolean logic'
  },
  {
    pattern: /'?\s*UNION\s+.*SELECT/gi,
    type: 'SQL Injection',
    severity: 'critical',
    description: 'UNION-based SQL injection'
  },
  {
    pattern: /;\s*(DROP|DELETE|UPDATE|INSERT|ALTER|TRUNCATE|CREATE|EXEC|EXECUTE)/gi,
    type: 'SQL Injection',
    severity: 'critical',
    description: 'Stacked query SQL injection'
  },
  {
    pattern: /\/\*.*\*\/|\-\-|\#/g,
    type: 'SQL Injection',
    severity: 'high',
    description: 'SQL comment-based injection'
  },
  {
    pattern: /(WAITFOR|SLEEP|DELAY|BENCHMARK|PG_SLEEP)\s*\(/gi,
    type: 'SQL Injection',
    severity: 'high',
    description: 'Time-based SQL injection'
  },
  {
    pattern: /(xp_cmdshell|sp_executesql|OPENROWSET|OPENQUERY)/gi,
    type: 'SQL Injection',
    severity: 'critical',
    description: 'SQL Server command execution'
  },
  {
    pattern: /(LOAD_FILE|INTO\s+OUTFILE|INTO\s+DUMPFILE)/gi,
    type: 'SQL Injection',
    severity: 'critical',
    description: 'MySQL file operation injection'
  }
];

/**
 * NoSQL Injection Patterns (MongoDB, etc.)
 */
const NOSQL_INJECTION_PATTERNS = [
  {
    pattern: /\$where.*[\+\-].*eval|function/gi,
    type: 'NoSQL Injection',
    severity: 'critical',
    description: 'MongoDB $where injection'
  },
  {
    pattern: /\$ne\s*:\s*null|\$gt\s*:|\$lt\s*:|\$in\s*:|\$nin\s*:/gi,
    type: 'NoSQL Injection',
    severity: 'high',
    description: 'MongoDB operator injection'
  },
  {
    pattern: /\{\s*\$regex\s*:|\{\s*\$where\s*:/gi,
    type: 'NoSQL Injection',
    severity: 'critical',
    description: 'MongoDB operator injection'
  },
  {
    pattern: /this\.(username|password|admin)\s*==/gi,
    type: 'NoSQL Injection',
    severity: 'high',
    description: 'MongoDB JavaScript injection'
  },
  {
    pattern: /;.*while.*\{.*\}/gi,
    type: 'NoSQL Injection',
    severity: 'high',
    description: 'DoS pattern in NoSQL query'
  }
];

/**
 * Command Injection Patterns
 */
const COMMAND_INJECTION_PATTERNS = [
  {
    pattern: /[;&|`$(){}[\]]/g,
    type: 'Command Injection',
    severity: 'high',
    description: 'Command separator detected'
  },
  {
    pattern: /;\s*(rm|del|delete|format|mkfs|shutdown|reboot)/gi,
    type: 'Command Injection',
    severity: 'critical',
    description: 'Destructive command detected'
  },
  {
    pattern: /\|\s*(nc|netcat|wget|curl|bash|sh|cmd|powershell)/gi,
    type: 'Command Injection',
    severity: 'critical',
    description: 'Network command chaining'
  },
  {
    pattern: /\$\(.*\)|`.*`|\$.*\{/g,
    type: 'Command Injection',
    severity: 'high',
    description: 'Command substitution syntax'
  },
  {
    pattern: /(cat|type|more|less|head|tail|grep|find)\s+.*\/etc\/|\/proc\/|\/sys\//gi,
    type: 'Command Injection',
    severity: 'high',
    description: 'Sensitive file access attempt'
  },
  {
    pattern: /(python|perl|ruby|node|php)\s+-[er]/gi,
    type: 'Command Injection',
    severity: 'high',
    description: 'Script execution attempt'
  }
];

/**
 * OS Shell Injection Patterns
 */
const OS_SHELL_INJECTION_PATTERNS = [
  {
    pattern: /(cmd\.exe|\/bin\/sh|\/bin\/bash|powershell|pwsh)/gi,
    type: 'OS Shell Injection',
    severity: 'critical',
    description: 'Shell execution attempt'
  },
  {
    pattern: /(subprocess|exec|system|popen|shell_exec|passthru|proc_open)/gi,
    type: 'OS Shell Injection',
    severity: 'critical',
    description: 'System command execution function'
  },
  {
    pattern: /(\.\/|\.\.\/|\/)(bin|usr|etc|var|tmp|opt)/gi,
    type: 'OS Shell Injection',
    severity: 'high',
    description: 'System path reference'
  }
];

/**
 * LDAP Injection Patterns
 */
const LDAP_INJECTION_PATTERNS = [
  {
    pattern: /[()&|!]/g,
    type: 'LDAP Injection',
    severity: 'high',
    description: 'LDAP operator detected'
  },
  {
    pattern: /\(.*\)\s*\(|\)\s*\(/g,
    type: 'LDAP Injection',
    severity: 'high',
    description: 'LDAP filter injection'
  },
  {
    pattern: /\(\|\s*\(|&\(|!\(/g,
    type: 'LDAP Injection',
    severity: 'high',
    description: 'LDAP boolean operator injection'
  },
  {
    pattern: /cn=\*|uid=\*|ou=\*/gi,
    type: 'LDAP Injection',
    severity: 'medium',
    description: 'LDAP wildcard injection'
  }
];

/**
 * XPath Injection Patterns
 */
const XPATH_INJECTION_PATTERNS = [
  {
    pattern: /'?\s*or\s+'?\d+'?\s*=\s*'?\d+'?/gi,
    type: 'XPath Injection',
    severity: 'high',
    description: 'XPath boolean injection'
  },
  {
    pattern: /'?\s*and\s+'?\d+'?\s*=\s*'?\d+'?/gi,
    type: 'XPath Injection',
    severity: 'high',
    description: 'XPath boolean injection'
  },
  {
    pattern: /\/\/|\.\.|@.*\[|\[.*\]/g,
    type: 'XPath Injection',
    severity: 'medium',
    description: 'XPath traversal syntax'
  },
  {
    pattern: /(union|or|and)\s+.*=/gi,
    type: 'XPath Injection',
    severity: 'high',
    description: 'XPath union/boolean injection'
  }
];

/**
 * XML Injection Patterns
 */
const XML_INJECTION_PATTERNS = [
  {
    pattern: /<!\[CDATA\[.*\]\]>/gi,
    type: 'XML Injection',
    severity: 'medium',
    description: 'CDATA section detected'
  },
  {
    pattern: /<!ENTITY/gi,
    type: 'XML Injection',
    severity: 'critical',
    description: 'XML entity declaration'
  },
  {
    pattern: /&[#\w]+;/g,
    type: 'XML Injection',
    severity: 'low',
    description: 'XML entity reference'
  },
  {
    pattern: /<script|javascript:|onerror=|onload=/gi,
    type: 'XML Injection',
    severity: 'high',
    description: 'Script injection in XML'
  }
];

/**
 * XXE (XML External Entity) Injection Patterns
 */
const XXE_INJECTION_PATTERNS = [
  {
    pattern: /<!ENTITY.*SYSTEM\s+["'](file|http|ftp|php|expect):/gi,
    type: 'XXE Injection',
    severity: 'critical',
    description: 'External entity reference detected'
  },
  {
    pattern: /<!DOCTYPE.*\[.*<!ENTITY/gi,
    type: 'XXE Injection',
    severity: 'critical',
    description: 'DTD with entity declaration'
  },
  {
    pattern: /&[\w]+;.*(file|http|ftp|php|expect):/gi,
    type: 'XXE Injection',
    severity: 'critical',
    description: 'Entity with external reference'
  }
];

/**
 * Template Injection (SSTI) Patterns
 */
const TEMPLATE_INJECTION_PATTERNS = [
  {
    pattern: /\{\{.*\}\}|\{%.*%\}|\#\{.*\}/g,
    type: 'Template Injection',
    severity: 'high',
    description: 'Template syntax detected'
  },
  {
    pattern: /\{\{.*(__import__|eval|exec|subprocess).*\}\}/gi,
    type: 'Template Injection',
    severity: 'critical',
    description: 'Dangerous template execution'
  },
  {
    pattern: /\{%.*import.*%\}|\{%.*include.*%\}/gi,
    type: 'Template Injection',
    severity: 'high',
    description: 'Template import/include'
  },
  {
    pattern: /\$\{.*\}|#\{.*\}/g,
    type: 'Template Injection',
    severity: 'high',
    description: 'Template variable syntax'
  }
];

/**
 * Expression Language Injection Patterns (EL, OGNL, SpEL)
 */
const EL_INJECTION_PATTERNS = [
  {
    pattern: /\$\{.*\}|\#\{.*\}|%\{.*\}/g,
    type: 'EL Injection',
    severity: 'high',
    description: 'Expression language syntax'
  },
  {
    pattern: /\$\{.*\(.*\).*\}/g,
    type: 'EL Injection',
    severity: 'high',
    description: 'EL function call'
  },
  {
    pattern: /\$\{.*\*.*\}|\$\{.*\/.*\}/g,
    type: 'EL Injection',
    severity: 'medium',
    description: 'EL arithmetic operation'
  }
];

/**
 * CRLF Injection Patterns
 */
const CRLF_INJECTION_PATTERNS = [
  {
    pattern: /\r\n|\n\r|\r|\n/g,
    type: 'CRLF Injection',
    severity: 'high',
    description: 'CRLF characters detected'
  },
  {
    pattern: /%0d%0a|%0a%0d|%0d|%0a/gi,
    type: 'CRLF Injection',
    severity: 'high',
    description: 'URL-encoded CRLF'
  },
  {
    pattern: /\r\n.*(Location|Set-Cookie|Content-Type):/gi,
    type: 'CRLF Injection',
    severity: 'critical',
    description: 'HTTP header injection via CRLF'
  }
];

/**
 * Host Header Injection Patterns
 */
const HOST_HEADER_INJECTION_PATTERNS = [
  {
    pattern: /Host:\s*[^\r\n]*(\.|localhost|127\.0\.0\.1)/gi,
    type: 'Host Header Injection',
    severity: 'high',
    description: 'Suspicious host header'
  },
  {
    pattern: /X-Forwarded-Host:\s*[^\r\n]*/gi,
    type: 'Host Header Injection',
    severity: 'medium',
    description: 'Forwarded host header'
  },
  {
    pattern: /X-Original-URL:\s*[^\r\n]*/gi,
    type: 'Host Header Injection',
    severity: 'medium',
    description: 'Original URL header'
  }
];

/**
 * HTTP Parameter Pollution Patterns
 */
const HPP_PATTERNS = [
  {
    pattern: /[?&][^=]+=.*[?&][^=]+=/g,
    type: 'HTTP Parameter Pollution',
    severity: 'medium',
    description: 'Multiple parameter values'
  },
  {
    pattern: /[?&].*=\w+.*[?&].*=.*/g,
    type: 'HTTP Parameter Pollution',
    severity: 'medium',
    description: 'Parameter pollution attempt'
  }
];

/**
 * SMTP Injection Patterns
 */
const SMTP_INJECTION_PATTERNS = [
  {
    pattern: /\r\n(MAIL|RCPT|DATA|QUIT|RSET|HELO|EHLO|VRFY|EXPN)/gi,
    type: 'SMTP Injection',
    severity: 'high',
    description: 'SMTP command injection'
  },
  {
    pattern: /(From|To|Subject|CC|BCC):.*\r\n/g,
    type: 'SMTP Injection',
    severity: 'high',
    description: 'SMTP header injection'
  },
  {
    pattern: /%0a.*(MAIL|RCPT|DATA)/gi,
    type: 'SMTP Injection',
    severity: 'high',
    description: 'URL-encoded SMTP injection'
  }
];

/**
 * JSON Injection Patterns
 */
const JSON_INJECTION_PATTERNS = [
  {
    pattern: /[{,]\s*"\w+"\s*:\s*["']?\s*<script/gi,
    type: 'JSON Injection',
    severity: 'high',
    description: 'Script injection in JSON'
  },
  {
    pattern: /[{,]\s*"\w+"\s*:\s*["']?\s*javascript:/gi,
    type: 'JSON Injection',
    severity: 'high',
    description: 'JavaScript protocol in JSON'
  },
  {
    pattern: /__proto__|constructor|prototype/gi,
    type: 'JSON Injection',
    severity: 'high',
    description: 'Prototype pollution attempt'
  }
];

/**
 * GraphQL Injection Patterns
 */
const GRAPHQL_INJECTION_PATTERNS = [
  {
    pattern: /\{.*(__schema|__type|__typename).*\}/g,
    type: 'GraphQL Injection',
    severity: 'medium',
    description: 'GraphQL introspection'
  },
  {
    pattern: /query\s*\{.*\{.*\{/g,
    type: 'GraphQL Injection',
    severity: 'medium',
    description: 'Nested GraphQL query'
  },
  {
    pattern: /(query|mutation|subscription).*\{\s*__/gi,
    type: 'GraphQL Injection',
    severity: 'medium',
    description: 'GraphQL introspection query'
  }
];

/**
 * Header Injection Patterns
 */
const HEADER_INJECTION_PATTERNS = [
  {
    pattern: /(Location|Set-Cookie|Content-Type|X-Forwarded-For):/gi,
    type: 'Header Injection',
    severity: 'high',
    description: 'HTTP header injection'
  },
  {
    pattern: /%0a.*:.*%0d/gi,
    type: 'Header Injection',
    severity: 'critical',
    description: 'CRLF header injection'
  }
];

/**
 * CSS Injection Patterns
 */
const CSS_INJECTION_PATTERNS = [
  {
    pattern: /expression\s*\(|javascript:|@import|url\s*\(/gi,
    type: 'CSS Injection',
    severity: 'high',
    description: 'CSS expression/import injection'
  },
  {
    pattern: /<script|onerror=|onload=/gi,
    type: 'CSS Injection',
    severity: 'high',
    description: 'Script in CSS'
  },
  {
    pattern: /\\[0-9a-f]{2}/gi,
    type: 'CSS Injection',
    severity: 'medium',
    description: 'CSS escape sequence'
  }
];

/**
 * HTML Injection Patterns
 */
const HTML_INJECTION_PATTERNS = [
  {
    pattern: /<script[^>]*>.*<\/script>/gi,
    type: 'HTML Injection',
    severity: 'critical',
    description: 'Script tag injection'
  },
  {
    pattern: /javascript:|data:text\/html|vbscript:/gi,
    type: 'HTML Injection',
    severity: 'critical',
    description: 'JavaScript protocol'
  },
  {
    pattern: /on\w+\s*=\s*["'][^"']*["']/gi,
    type: 'HTML Injection',
    severity: 'critical',
    description: 'Event handler injection'
  },
  {
    pattern: /<iframe|<embed|<object|<svg.*onload/gi,
    type: 'HTML Injection',
    severity: 'high',
    description: 'Iframe/embed injection'
  }
];

/**
 * DOM Clobbering Patterns
 */
const DOM_CLOBBERING_PATTERNS = [
  {
    pattern: /(id|name)\s*=\s*["'](document|window|location|history)["']/gi,
    type: 'DOM Clobbering',
    severity: 'high',
    description: 'DOM property clobbering'
  },
  {
    pattern: /<form[^>]*name\s*=\s*["']submit["']/gi,
    type: 'DOM Clobbering',
    severity: 'medium',
    description: 'Form clobbering'
  }
];

/**
 * Deserialization Injection Patterns
 */
const DESERIALIZATION_PATTERNS = [
  {
    pattern: /(ObjectInputStream|pickle|yaml\.load|json\.loads|eval|unserialize)/gi,
    type: 'Deserialization Injection',
    severity: 'critical',
    description: 'Unsafe deserialization function'
  },
  {
    pattern: /__reduce__|__setstate__|__getstate__/gi,
    type: 'Deserialization Injection',
    severity: 'critical',
    description: 'Python pickle magic methods'
  },
  {
    pattern: /readObject|readResolve|readExternal/gi,
    type: 'Deserialization Injection',
    severity: 'critical',
    description: 'Java deserialization methods'
  }
];

/**
 * Path Traversal Patterns
 */
const PATH_TRAVERSAL_PATTERNS = [
  {
    pattern: /\.\.\/|\.\.\\|\.\.%2f|\.\.%5c/gi,
    type: 'Path Traversal',
    severity: 'critical',
    description: 'Directory traversal attempt'
  },
  {
    pattern: /\/etc\/passwd|\/etc\/shadow|\/proc\/self|\/Windows\/System32/gi,
    type: 'Path Traversal',
    severity: 'critical',
    description: 'Sensitive file path'
  },
  {
    pattern: /(\.\.\/)+[\w\/]+/g,
    type: 'Path Traversal',
    severity: 'high',
    description: 'Multiple directory traversal'
  },
  {
    pattern: /%2e%2e%2f|%2e%2e%5c|%252e%252e%252f/gi,
    type: 'Path Traversal',
    severity: 'high',
    description: 'URL-encoded path traversal'
  }
];

/**
 * YAML Injection Patterns
 */
const YAML_INJECTION_PATTERNS = [
  {
    pattern: /!!python\/object|!!python\/name|!!python\/module/gi,
    type: 'YAML Injection',
    severity: 'critical',
    description: 'Python YAML object injection'
  },
  {
    pattern: /!!java|!!javax|!!org\.springframework/gi,
    type: 'YAML Injection',
    severity: 'critical',
    description: 'Java YAML object injection'
  },
  {
    pattern: /<%.*%>|\$\{.*\}/g,
    type: 'YAML Injection',
    severity: 'high',
    description: 'Template in YAML'
  }
];

/**
 * CSV Formula Injection Patterns
 */
const CSV_FORMULA_INJECTION_PATTERNS = [
  {
    pattern: /^[=+\-@]/,
    type: 'CSV Formula Injection',
    severity: 'high',
    description: 'CSV formula injection'
  },
  {
    pattern: /^[=+\-@].*(HYPERLINK|WEBSERVICE|IMPORTXML|IMPORTDATA)/gi,
    type: 'CSV Formula Injection',
    severity: 'critical',
    description: 'Dangerous CSV formula'
  },
  {
    pattern: /^[=+\-@].*cmd\.exe|^[=+\-@].*powershell/gi,
    type: 'CSV Formula Injection',
    severity: 'critical',
    description: 'Command execution in CSV'
  }
];

/**
 * Regex Injection (ReDoS) Patterns
 */
const REGEX_INJECTION_PATTERNS = [
  {
    pattern: /\(\.\*\)\+\+|\(\.\*\)\+\?|\(\.\+\)\+\+/g,
    type: 'Regex Injection',
    severity: 'high',
    description: 'ReDoS vulnerable pattern'
  },
  {
    pattern: /\([^)]*\)\+\+[^)]*\)\+\+/g,
    type: 'Regex Injection',
    severity: 'high',
    description: 'Nested quantifier ReDoS'
  },
  {
    pattern: /\(a\+\)\+b|\(\.\*\)\{100,\}/g,
    type: 'Regex Injection',
    severity: 'medium',
    description: 'Potential ReDoS pattern'
  }
];

/**
 * Prototype Pollution Patterns
 */
const PROTOTYPE_POLLUTION_PATTERNS = [
  {
    pattern: /__proto__|constructor\[|constructor\.prototype/gi,
    type: 'Prototype Pollution',
    severity: 'critical',
    description: 'Prototype pollution attempt'
  },
  {
    pattern: /\.__proto__\s*=|\.constructor\.prototype\s*=/g,
    type: 'Prototype Pollution',
    severity: 'critical',
    description: 'Prototype assignment'
  },
  {
    pattern: /\["__proto__"\]|\['__proto__'\]/g,
    type: 'Prototype Pollution',
    severity: 'high',
    description: 'Prototype bracket notation'
  }
];

/**
 * Format String Injection Patterns
 */
const FORMAT_STRING_INJECTION_PATTERNS = [
  {
    pattern: /%[dxosXOScfeEgGpnaA].*%[dxosXOScfeEgGpnaA]/g,
    type: 'Format String Injection',
    severity: 'high',
    description: 'Format string specifiers'
  },
  {
    pattern: /%(?:[0-9]+\$)?[dxosXOScfeEgGpnaA]/g,
    type: 'Format String Injection',
    severity: 'high',
    description: 'Positional format string'
  },
  {
    pattern: /%n|%hn|%hhn|%lln/g,
    type: 'Format String Injection',
    severity: 'critical',
    description: 'Write format specifier'
  }
];

/**
 * Unicode/Homoglyph Injection Patterns
 */
const UNICODE_INJECTION_PATTERNS = [
  {
    pattern: /[\u0430-\u044f\u0400-\u04ff\u0500-\u052f]/g, // Cyrillic
    type: 'Unicode Injection',
    severity: 'medium',
    description: 'Cyrillic homoglyph'
  },
  {
    pattern: /[\u0391-\u03ff]/g, // Greek
    type: 'Unicode Injection',
    severity: 'medium',
    description: 'Greek homoglyph'
  },
  {
    pattern: /[\uff00-\uffef]/g, // Halfwidth/Fullwidth
    type: 'Unicode Injection',
    severity: 'low',
    description: 'Fullwidth character'
  }
];

/**
 * SSI Injection Patterns
 */
const SSI_INJECTION_PATTERNS = [
  {
    pattern: /<!--#(?:include|exec|echo|config|fsize|flastmod|exec|set)/gi,
    type: 'SSI Injection',
    severity: 'critical',
    description: 'Server-Side Include injection'
  },
  {
    pattern: /<!--#include\s+virtual|<!--#exec\s+cmd/gi,
    type: 'SSI Injection',
    severity: 'critical',
    description: 'SSI command execution'
  }
];

/**
 * Log Injection Patterns
 */
const LOG_INJECTION_PATTERNS = [
  {
    pattern: /\r\n|\n/g,
    type: 'Log Injection',
    severity: 'medium',
    description: 'Newline in log entry'
  },
  {
    pattern: /(ERROR|WARN|INFO|DEBUG|TRACE).*\(/gi,
    type: 'Log Injection',
    severity: 'low',
    description: 'Log level injection'
  }
];

/**
 * Comprehensive injection detection function
 *
 * @param input - Input string to check
 * @param context - Context where input will be used (e.g., 'query', 'url', 'header', 'curl')
 * @returns Detection result with threats and severity
 */
export const detectInjections = (
  input: string,
  context: 'query' | 'url' | 'header' | 'body' | 'filename' | 'general' | 'curl' = 'general'
): InjectionDetectionResult => {
  if (!input || typeof input !== 'string') {
    return {
      isSafe: true,
      threats: [],
      severity: 'safe'
    };
  }

  const normalizedInput = input.trim();
  const threats: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    pattern: string;
    description: string;
  }> = [];

  // Collect all patterns based on context
  const allPatterns = [
    ...SQL_INJECTION_PATTERNS,
    ...NOSQL_INJECTION_PATTERNS,
    ...COMMAND_INJECTION_PATTERNS,
    ...OS_SHELL_INJECTION_PATTERNS,
    ...LDAP_INJECTION_PATTERNS,
    ...XPATH_INJECTION_PATTERNS,
    ...XML_INJECTION_PATTERNS,
    ...XXE_INJECTION_PATTERNS,
    ...TEMPLATE_INJECTION_PATTERNS,
    ...EL_INJECTION_PATTERNS,
    ...CRLF_INJECTION_PATTERNS,
    ...HOST_HEADER_INJECTION_PATTERNS,
    ...HPP_PATTERNS,
    ...SMTP_INJECTION_PATTERNS,
    ...JSON_INJECTION_PATTERNS,
    ...GRAPHQL_INJECTION_PATTERNS,
    ...HEADER_INJECTION_PATTERNS,
    ...CSS_INJECTION_PATTERNS,
    ...HTML_INJECTION_PATTERNS,
    ...DOM_CLOBBERING_PATTERNS,
    ...DESERIALIZATION_PATTERNS,
    ...PATH_TRAVERSAL_PATTERNS,
    ...YAML_INJECTION_PATTERNS,
    ...CSV_FORMULA_INJECTION_PATTERNS,
    ...REGEX_INJECTION_PATTERNS,
    ...PROTOTYPE_POLLUTION_PATTERNS,
    ...FORMAT_STRING_INJECTION_PATTERNS,
    ...UNICODE_INJECTION_PATTERNS,
    ...SSI_INJECTION_PATTERNS,
    ...LOG_INJECTION_PATTERNS
  ];

  // Context-specific filtering
  let patternsToCheck = allPatterns;
  if (context === 'curl') {
    // cURL commands legitimately contain URLs, headers, data payloads, etc.
    // Only check for truly dangerous patterns that shouldn't appear in ANY cURL command
    patternsToCheck = [
      // Only check for actual malicious payloads, not cURL syntax
      ...XXE_INJECTION_PATTERNS,  // XML entity attacks
      ...DESERIALIZATION_PATTERNS.filter(p =>
        p.description !== 'Unsafe deserialization function' // Allow function names in API payloads
      ),
      ...SSI_INJECTION_PATTERNS,  // Server-side include attacks
      // Check for actual script injection in data payloads (but not HTML tag patterns that might be in API data)
      {
        pattern: /<script[^>]*>.*<\/script>/gi,
        type: 'HTML Injection',
        severity: 'critical' as const,
        description: 'Script tag injection'
      }
    ];
  } else if (context === 'filename') {
    patternsToCheck = [
      ...PATH_TRAVERSAL_PATTERNS,
      ...COMMAND_INJECTION_PATTERNS,
      ...HTML_INJECTION_PATTERNS
    ];
  } else if (context === 'header') {
    patternsToCheck = [
      ...HEADER_INJECTION_PATTERNS,
      ...CRLF_INJECTION_PATTERNS,
      ...HOST_HEADER_INJECTION_PATTERNS
    ];
  } else if (context === 'url') {
    patternsToCheck = [
      ...PATH_TRAVERSAL_PATTERNS,
      ...HTML_INJECTION_PATTERNS,
      ...CRLF_INJECTION_PATTERNS,
      ...SQL_INJECTION_PATTERNS
    ];
  } else if (context === 'query' || context === 'sql') {
    // SQL queries legitimately contain semicolons, parentheses, and other syntax
    // Only check for truly dangerous patterns like destructive SQL commands
    patternsToCheck = [
      // Check for destructive SQL operations that modify data
      ...SQL_INJECTION_PATTERNS.filter(p =>
        p.severity === 'critical' ||
        p.description?.includes('DROP') ||
        p.description?.includes('DELETE') ||
        p.description?.includes('TRUNCATE') ||
        p.description?.includes('command execution')
      ),
      // Skip command injection patterns as semicolons/parens are valid SQL syntax
      // Skip NoSQL patterns as this is for SQL queries
      ...XXE_INJECTION_PATTERNS,  // XML entity attacks if XML is in query
    ];
  }

  // Check all patterns
  for (const { pattern, type, severity, description } of patternsToCheck) {
    if (pattern.test(normalizedInput)) {
      threats.push({
        type,
        severity,
        pattern: pattern.toString(),
        description
      });
    }
  }

  // Determine overall severity
  let overallSeverity: 'safe' | 'low' | 'medium' | 'high' | 'critical' = 'safe';
  if (threats.length > 0) {
    const severities = threats.map(t => t.severity);
    if (severities.includes('critical')) {
      overallSeverity = 'critical';
    } else if (severities.includes('high')) {
      overallSeverity = 'high';
    } else if (severities.includes('medium')) {
      overallSeverity = 'medium';
    } else {
      overallSeverity = 'low';
    }
  }

  const isSafe = threats.length === 0;

  if (!isSafe) {
    logger.warn('SECURITY', 'Injection attack detected', {
      context,
      threatCount: threats.length,
      severity: overallSeverity,
      threats: threats.map(t => t.type)
    });
  }

  return {
    isSafe,
    threats,
    severity: overallSeverity
  };
};

/**
 * Sanitize input to prevent injection attacks
 * 
 * @param input - Input to sanitize
 * @param context - Context of input
 * @returns Sanitized input
 */
export const sanitizeInput = (
  input: string,
  context: 'query' | 'url' | 'header' | 'body' | 'filename' | 'general' | 'curl' = 'general'
): string => {
  if (!input || typeof input !== 'string') return '';

  let sanitized = input;

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');

  // Remove control characters (except newlines and tabs for some contexts)
  if (context !== 'body' && context !== 'query') {
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
  }

  // Context-specific sanitization
  if (context === 'filename') {
    // Remove path separators
    sanitized = sanitized.replace(/[\/\\:\*\?"<>\|]/g, '_');
    // Remove leading dots
    sanitized = sanitized.replace(/^\.+/, '');
  }

  if (context === 'url') {
    // Encode special URL characters
    try {
      sanitized = encodeURIComponent(sanitized);
    } catch (e) {
      // If encoding fails, remove dangerous chars
      sanitized = sanitized.replace(/[<>"']/g, '');
    }
  }

  return sanitized;
};

